import React from 'react'
import {
    Accordion,
    Alert,
    Button,
    Card,
    Col,
    FormControl,
    FormGroup,
    FormLabel,
    Modal,
    ModalTitle,
    Row,
    Spinner
} from 'react-bootstrap'
import './ReferralList.css'

const ReferralDetailsDialog = ({
                                   onCloseClicked,
                                   referralContent,
                                   contentLoading,
                                   contentError,
                                   referralDetails,
                                   detailsLoading,
                                   detailsError,
                                   referralId
                               }) => {

    const formatDate = (datum, showTime = false) => {
        if (!datum) return 'Okänt datum'
        const date = new Date(datum)
        const dateStr = date.toLocaleDateString('sv-SE')
        if (showTime) {
            return dateStr + ' ' + date.toLocaleTimeString('sv-SE', {
                hour: '2-digit',
                minute: '2-digit'
            })
        }
        return dateStr
    }

    // Parse klinisk information into structured fields
    const parseKliniskInformation = (text) => {
        if (!text) return {}

        const result = {
            interpreterNeeded: null,
            interpreterLanguage: '',
            photoSent: null,
            description: '',
            duration: '',
            receivedCorrectionToday: null,
            resultToday: {right: {}, left: {}},
            resultLastExamination: {right: {}, left: {}}
        }

        const tolkMatch = text.match(/BEHOV AV TOLK:\s*(Ja|Nej)/i)
        if (tolkMatch) result.interpreterNeeded = tolkMatch[1].toLowerCase() === 'ja'

        const sprakMatch = text.match(/SPRÅK:\s*([^\n]+)/)
        if (sprakMatch) result.interpreterLanguage = sprakMatch[1].trim()

        const fotoMatch = text.match(/FOTO INSKICKAT:\s*(Ja|Nej)/i)
        if (fotoMatch) result.photoSent = fotoMatch[1].toLowerCase() === 'ja'

        const beskrivningMatch = text.match(/DETALJERAD BESKRIVNING AV PATIENTENS BESVÄR:\s*([\s\S]*?)(?=HUR LÄNGE HAR PATIENTEN|$)/i)
        if (beskrivningMatch) result.description = beskrivningMatch[1].trim()

        const varaktighetMatch = text.match(/HUR LÄNGE HAR PATIENTEN HAFT BESVÄR:\s*([\s\S]*?)(?=RESULTAT VID DAGENS|$)/i)
        if (varaktighetMatch) result.duration = varaktighetMatch[1].trim()

        const korrektionMatch = text.match(/ERHÅLLIT KORREKTION VID DAGENS BESÖK:\s*(Ja|Nej)/i)
        if (korrektionMatch) result.receivedCorrectionToday = korrektionMatch[1].toLowerCase() === 'ja'

        const idagMatch = text.match(/RESULTAT VID DAGENS UNDERSÖKNING:\s*([\s\S]*?)(?=ERHÅLLIT KORREKTION|$)/i)
        if (idagMatch) {
            result.resultToday = parseEyeResults(idagMatch[1])
        }

        const foregaendeMatch = text.match(/RESULTAT VID FÖREGÅENDE UNDERSÖKNING:[^\n]*\n+([\s\S]*?)$/i)
        if (foregaendeMatch) {
            result.resultLastExamination = parseEyeResults(foregaendeMatch[1])
        }

        return result
    }

    const parseEyeResults = (text) => {
        const result = {right: {}, left: {}}

        // Parse HÖGER - handle variable spacing/newlines
        const hogerMatch = text.match(/HÖGER:\s*\n[^\n]*\n([^\n]+)/)
        if (hogerMatch) {
            const values = hogerMatch[1].split('\t')
            result.right = {
                freeVisus: values[0]?.trim() || '',
                bestCorrectionSphere: values[1]?.trim() || '',
                bestCorrectionCyl: values[2]?.trim() || '',
                bestCorrectionAx: values[3]?.trim() || '',
                visus: values[4]?.trim() || '',
                visusAddStph: values[5]?.trim() || '',
                pressure: values[6]?.trim() || ''
            }
        }

        // Parse VÄNSTER
        const vansterMatch = text.match(/VÄNSTER:\s*\n[^\n]*\n([^\n]+)/)
        if (vansterMatch) {
            const values = vansterMatch[1].split('\t')
            result.left = {
                freeVisus: values[0]?.trim() || '',
                bestCorrectionSphere: values[1]?.trim() || '',
                bestCorrectionCyl: values[2]?.trim() || '',
                bestCorrectionAx: values[3]?.trim() || '',
                visus: values[4]?.trim() || '',
                visusAddStph: values[5]?.trim() || '',
                pressure: values[6]?.trim() || ''
            }
        }

        return result
    }

    const hasContent = referralContent && (
        referralContent.fragestallning ||
        referralContent.kliniskInformation ||
        referralContent.externtRemissId
    )

    const hasAnswers = referralDetails &&
        ((referralDetails.clinicalAnswers && referralDetails.clinicalAnswers.length > 0) ||
            (referralDetails.triageAnswers && referralDetails.triageAnswers.length > 0) ||
            (referralDetails.complementaryRequests && referralDetails.complementaryRequests.length > 0) ||
            (referralDetails.rejections && referralDetails.rejections.length > 0))

    const parsed = referralContent?.kliniskInformation
        ? parseKliniskInformation(referralContent.kliniskInformation)
        : {}

    const isLoading = contentLoading || detailsLoading
    const hasError = contentError || detailsError

    // Get all answers sorted by date (newest first)
    const getAllAnswersSorted = () => {
        if (!referralDetails) return []
        const clinical = (referralDetails.clinicalAnswers || []).map(a => ({...a, _type: 'clinical'}))
        const triage = (referralDetails.triageAnswers || []).map(a => ({...a, _type: 'triage'}))
        const requests = (referralDetails.complementaryRequests || []).map(r => ({...r, _type: 'request'}))
        const rejections = (referralDetails.rejections || []).map(a => ({...a, _type: 'rejection'}))
        return [...clinical, ...triage, ...requests, ...rejections].sort((a, b) => {
            const dateA = a.date ? new Date(a.date) : new Date(0)
            const dateB = b.date ? new Date(b.date) : new Date(0)
            return dateB - dateA
        })
    }

    const getBorderClass = (answer) => {
        if (answer.cancelled) return 'border-danger'
        if (answer._type === 'rejection') return 'border-danger'
        if (answer._type === 'request') return 'border-warning'
        if (answer._type === 'triage') return 'border-info'
        if (answer.answerType === 'FIN') return 'border-primary'
        return 'border-warning'
    }

    const getHeaderClass = (answer) => {
        if (answer.cancelled) return 'bg-danger bg-opacity-25'
        if (answer._type === 'rejection') return 'bg-danger bg-opacity-10'
        if (answer._type === 'request') return 'bg-warning bg-opacity-25'
        if (answer._type === 'triage') return 'bg-info bg-opacity-10'
        if (answer.answerType === 'FIN') return 'bg-primary bg-opacity-10'
        return 'bg-warning bg-opacity-10'
    }

    const getBadgeClass = (answer) => {
        if (answer._type === 'rejection') return 'bg-danger'
        if (answer._type === 'request') return 'bg-warning'
        if (answer._type === 'triage') return 'bg-info'
        if (answer.answerType === 'FIN') return 'bg-primary'
        return 'bg-warning'
    }

    const showAuthor = (answer) => {
        const author = answer.answeredBy || answer.requestedBy || answer.rejectedBy
        return author && author !== 'Unknown' && author !== 'Okänd'
    }

    const getAuthor = (answer) => {
        return answer.answeredBy || answer.requestedBy || answer.rejectedBy
    }

    const renderResultMatrix = (result, title) => (
        <div className="mb-3">
            <Row>
                <Col xs={12}><b>{title}</b></Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <Card className="panel-default mt-2">
                        <Row className="p-2">
                            <Col xs={12} className="resultMatrix">
                                {/* Header Row 1 */}
                                <Row className="resultHeader text-center small">
                                    <Col xs={1}></Col>
                                    <Col xs={11}>
                                        <Row>
                                            <Col xs={2}>Fri</Col>
                                            <Col xs={5} className="borderColumn">Bästa korrektion</Col>
                                            <Col xs={5} className="borderColumn">
                                                <Row>
                                                    <Col xs={4}></Col>
                                                    <Col xs={4}>Add</Col>
                                                    <Col xs={4}></Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                {/* Header Row 2 */}
                                <Row className="resultHeader text-center small">
                                    <Col xs={1}></Col>
                                    <Col xs={11}>
                                        <Row>
                                            <Col xs={2}>Visus</Col>
                                            <Col xs={5} className="borderColumn">
                                                <Row>
                                                    <Col xs={4}>Sfär</Col>
                                                    <Col xs={4}>Cyl</Col>
                                                    <Col xs={4}>Ax</Col>
                                                </Row>
                                            </Col>
                                            <Col xs={5} className="borderColumn">
                                                <Row>
                                                    <Col xs={4}>Visus</Col>
                                                    <Col xs={4}>stph</Col>
                                                    <Col xs={4}>Tryck</Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                {/* Höger */}
                                <Row className="mt-1">
                                    <Col xs={1} className="resultHeader d-flex align-items-center"><b>Höger</b></Col>
                                    <Col xs={11}>
                                        <Row>
                                            <Col xs={2} className="referralValueCol">
                                                <FormControl size="sm" readOnly value={result.right?.freeVisus || ''}/>
                                            </Col>
                                            <Col xs={5} className="borderColumn referralValueCol">
                                                <Row className="ps-2 pe-2">
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.right?.bestCorrectionSphere || ''}/></Col>
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.right?.bestCorrectionCyl || ''}/></Col>
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.right?.bestCorrectionAx || ''}/></Col>
                                                </Row>
                                            </Col>
                                            <Col xs={5} className="borderColumn referralValueCol">
                                                <Row className="ps-2 pe-2">
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.right?.visus || ''}/></Col>
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.right?.visusAddStph || ''}/></Col>
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.right?.pressure || ''}/></Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                {/* Vänster */}
                                <Row className="mt-1">
                                    <Col xs={1} className="resultHeader d-flex align-items-center"><b>Vänster</b></Col>
                                    <Col xs={11}>
                                        <Row>
                                            <Col xs={2} className="referralValueCol">
                                                <FormControl size="sm" readOnly value={result.left?.freeVisus || ''}/>
                                            </Col>
                                            <Col xs={5} className="borderColumn referralValueCol">
                                                <Row className="ps-2 pe-2">
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.left?.bestCorrectionSphere || ''}/></Col>
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.left?.bestCorrectionCyl || ''}/></Col>
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.left?.bestCorrectionAx || ''}/></Col>
                                                </Row>
                                            </Col>
                                            <Col xs={5} className="borderColumn referralValueCol">
                                                <Row className="ps-2 pe-2">
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.left?.visus || ''}/></Col>
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.left?.visusAddStph || ''}/></Col>
                                                    <Col xs={4} style={{padding: '1px'}}><FormControl size="sm" readOnly
                                                                                                      value={result.left?.pressure || ''}/></Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    )

    const renderReferralContent = () => (
        <div className="Referral">
            {/* Tolk & Foto row */}
            <Row className="mb-3">
                <Col sm={12} md={8}>
                    <FormGroup>
                        <Row>
                            <Col sm={5}>
                                <Row>
                                    <Col><FormLabel>Krävs tolk?</FormLabel></Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <FormControl readOnly
                                                     value={parsed.interpreterNeeded === true ? 'JA' : parsed.interpreterNeeded === false ? 'NEJ' : 'Ej angivet'}/>
                                    </Col>
                                </Row>
                            </Col>
                            <Col sm={7}>
                                <FormGroup>
                                    <FormLabel>Språk</FormLabel>
                                    <FormControl readOnly value={parsed.interpreterLanguage || ''}/>
                                </FormGroup>
                            </Col>
                        </Row>
                    </FormGroup>
                </Col>
                <Col sm={12} md={4}>
                    <FormGroup>
                        <FormLabel>Foto inskickat?</FormLabel>
                        <FormControl readOnly
                                     value={parsed.photoSent === true ? 'JA' : parsed.photoSent === false ? 'NEJ' : 'Ej angivet'}/>
                    </FormGroup>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12}>
                    <FormGroup>
                        <FormLabel>Frågeställning</FormLabel>
                        <FormControl readOnly value={referralContent.fragestallning || ''}/>
                    </FormGroup>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12}>
                    <FormGroup>
                        <FormLabel>Detaljerad beskrivning av patientens besvär</FormLabel>
                        <FormControl as="textarea" rows={7} readOnly value={parsed.description || ''}/>
                    </FormGroup>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12}>
                    <FormGroup>
                        <FormLabel>Hur länge har patienten haft sina besvär?</FormLabel>
                        <FormControl readOnly value={parsed.duration || ''}/>
                    </FormGroup>
                </Col>
            </Row>

            {renderResultMatrix(parsed.resultToday, 'Resultat vid dagens undersökning')}

            <Row className="mb-3">
                <Col sm={5}>
                    <FormLabel>Erhållit korrektion vid dagens besök?</FormLabel>
                </Col>
                <Col sm={7}>
                    <FormControl readOnly
                                 value={parsed.receivedCorrectionToday === true ? 'JA' : parsed.receivedCorrectionToday === false ? 'NEJ' : 'Ej angivet'}/>
                </Col>
            </Row>

            {renderResultMatrix(parsed.resultLastExamination, 'Resultat vid föregående undersökning')}
        </div>
    )

    const renderAnswerContent = (answer) => (
        <>
            {answer._type === 'clinical' && (
                <>
                    {answer.assessment &&
                        <div className="mb-3">
                            <strong>Bedömning:</strong>
                            <div className="answer-text">{answer.assessment}</div>
                        </div>
                    }
                    {answer.sampling &&
                        <div className="mb-3">
                            <strong>Provtagning:</strong>
                            <div className="answer-text">{answer.sampling}</div>
                        </div>
                    }
                    {answer.other &&
                        <div className="mb-3">
                            <strong>Övrigt:</strong>
                            <div className="answer-text">{answer.other}</div>
                        </div>
                    }
                    {answer.planning &&
                        <div className="mb-3">
                            <strong>Planering:</strong>
                            <div className="answer-text">{answer.planning}</div>
                        </div>
                    }
                    {answer.additionalInformation &&
                        <div className="mb-3">
                            <strong>Övrig information:</strong>
                            <div className="answer-text">{answer.additionalInformation}</div>
                        </div>
                    }
                    {answer.diagnoses && answer.diagnoses.length > 0 &&
                        <div className="mb-3">
                            <strong>Diagnos:</strong>
                            <ul className="answer-list">
                                {answer.diagnoses.map((diagnos, i) => (
                                    <li key={i}>{diagnos}</li>
                                ))}
                            </ul>
                        </div>
                    }
                    {answer.actions && answer.actions.length > 0 &&
                        <div className="mb-3">
                            <strong>Åtgärd:</strong>
                            <ul className="answer-list">
                                {answer.actions.map((action, i) => (
                                    <li key={i}>{action}</li>
                                ))}
                            </ul>
                        </div>
                    }
                </>
            )}
            {answer._type === 'triage' && (
                <>
                    {answer.assessment &&
                        <div className="mb-3">
                            <strong>Bedömning:</strong>
                            <div className="answer-text">{answer.assessment}</div>
                        </div>
                    }
                    {answer.medicalPriority &&
                        <div className="row mb-2 align-items-start">
                            <div className="col-sm-3"><strong>Medicinsk prioritet:</strong></div>
                            <div className="col-sm-9">{answer.medicalPriority}</div>
                        </div>
                    }
                    {answer.monitoringInterval &&
                        <div className="row mb-2 align-items-start">
                            <div className="col-sm-3"><strong>Bevakningsintervall:</strong></div>
                            <div className="col-sm-9">{answer.monitoringInterval}</div>
                        </div>
                    }
                    {answer.directClosure &&
                        <div className="row mb-2 align-items-start">
                            <div className="col-sm-3"><strong>Direktavslut:</strong></div>
                            <div className="col-sm-9">{answer.directClosure}</div>
                        </div>
                    }
                    {answer.preVisitInfo && answer.preVisitInfo.length > 0 &&
                        <div className="mb-3">
                            <strong>Inför besöket:</strong>
                            <ul className="answer-list">
                                {answer.preVisitInfo.map((info, i) => (
                                    <li key={i}>{info}</li>
                                ))}
                            </ul>
                        </div>
                    }
                </>
            )}
            {answer._type === 'request' && (
                <>
                    {answer.reason &&
                        <div className="mb-3">
                            <strong>Anledning till begäran om komplettering:</strong>
                            <div className="answer-text">{answer.reason}</div>
                        </div>
                    }
                </>
            )}
            {answer._type === 'rejection' && (
                <>
                    {answer.rejectionReason &&
                        <div className="mb-3">
                            <strong>Avvisningsorsak:</strong>
                            <div className="answer-text">{answer.rejectionReason}</div>
                        </div>
                    }
                    {answer.comment &&
                        <div className="mb-3">
                            <strong>Kommentar:</strong>
                            <div className="answer-text">{answer.comment}</div>
                        </div>
                    }
                </>
            )}
        </>
    )

    // Build timeline items combining referral content and answers
    const buildTimelineItems = () => {
        const items = []

        // Add original referral content as first item
        if (hasContent) {
            items.push({
                _type: 'referral',
                date: referralContent.skickatDatum,
                content: referralContent
            })
        }

        // Add all answers
        if (hasAnswers) {
            const answers = getAllAnswersSorted()
            answers.forEach(answer => {
                items.push(answer)
            })
        }

        // Sort by date (newest first for answers, but referral always shows with its date)
        items.sort((a, b) => {
            const dateA = a.date || a.skickatDatum ? new Date(a.date || a.skickatDatum) : new Date(0)
            const dateB = b.date || b.skickatDatum ? new Date(b.date || b.skickatDatum) : new Date(0)
            return dateB - dateA
        })

        return items
    }

    const timelineItems = buildTimelineItems()

    return (
        <Modal
            show={true}
            onHide={onCloseClicked}
            backdrop="static"
            keyboard={true}
            size="xl"
            scrollable
            className="ReferralDetailsDialog"
        >
            <Modal.Header closeButton>
                <ModalTitle><h3>Remissdetaljer</h3></ModalTitle>
            </Modal.Header>
            <Modal.Body>
                {isLoading &&
                    <div className="text-center p-4">
                        <Spinner animation="border" variant="primary" className="mb-2"/>
                        <div>Hämtar remissinformation...</div>
                    </div>
                }

                {hasError &&
                    <Alert variant="danger">
                        <strong>Fel:</strong> {contentError || detailsError}
                    </Alert>
                }

                {!isLoading && !hasError && !hasContent && !hasAnswers &&
                    <Alert variant="info">
                        Ingen remissinformation tillgänglig.
                    </Alert>
                }

                {!isLoading && (hasContent || hasAnswers) &&
                    <Accordion defaultActiveKey={['0']} alwaysOpen>
                        {timelineItems.map((item, index) => {
                            if (item._type === 'referral') {
                                return (
                                    <Accordion.Item
                                        key={`referral-${index}`}
                                        eventKey={String(index)}
                                        className="mb-2 border-success"
                                    >
                                        <Accordion.Header className="bg-success bg-opacity-10">
                                            <div className="d-flex flex-wrap align-items-center gap-2">
                                                <strong>{formatDate(item.date)}</strong>
                                                <span className="badge bg-success">Remiss skickad</span>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            {renderReferralContent()}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                )
                            } else {
                                return (
                                    <Accordion.Item
                                        key={`answer-${index}`}
                                        eventKey={String(index)}
                                        className={`mb-2 ${getBorderClass(item)}`}
                                    >
                                        <Accordion.Header className={getHeaderClass(item)}>
                                            <div className="d-flex flex-wrap align-items-center gap-2">
                                                <strong>{formatDate(item.date, true)}</strong>
                                                {showAuthor(item) &&
                                                    <span className="text-muted">• {getAuthor(item)}</span>
                                                }
                                                <span className={`badge ${getBadgeClass(item)}`}>
                                                    {item.answerTypeDisplay}
                                                </span>
                                                {item.cancelled &&
                                                    <span className="badge bg-danger">Makulerad</span>
                                                }
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            {renderAnswerContent(item)}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                )
                            }
                        })}
                    </Accordion>
                }
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <small style={{color: '#3982f7'}}>{referralId}</small>
                <Button variant="danger" onClick={onCloseClicked}>Stäng</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ReferralDetailsDialog
